import { useLocation, useNavigate } from "react-router-dom";
import queryString from "query-string";
import { useCallback, useContext } from "react";
import { useQuery } from "@apollo/client";

import { AuthContext } from "src/context/auth";
import { getScreeningCases } from "src/types/generated/graphQLTypesOld";
import GET_SCREENING_CASES_QUERY from "containers/CheckCaseIdWrapper/getScreeningCases.gql";
import { ROUTES } from "src/constants";
import { createQueryParams } from "src/utils/createQueryParams";
import { isKonfirRedirectPage } from "src/utils";

type ScreeningCases = getScreeningCases["screeningCases"];

type UsePageFallbackCaseIdRedirect = { isLoading: boolean };

export const usePageFallbackCaseIdRedirect =
  (): UsePageFallbackCaseIdRedirect => {
    const navigateTo = useNavigate();

    const { pathname, search } = useLocation();
    const queryParams = queryString.parse(search);

    const { isAuthenticated } = useContext(AuthContext);
    const shouldCallCasesRequest = isAuthenticated && !queryParams.caseId;

    const redirectToFallbackCaseId = useCallback(
      (cases: ScreeningCases) => {
        const sortedCases = cases?.sort(
          (a, b) => b.whenCreated - a.whenCreated
        );
        const lastCaseId = sortedCases?.[0]?.id ?? "";
        const shouldRedirectToFallbackCaseId =
          isAuthenticated &&
          !queryParams.caseId &&
          lastCaseId &&
          !isKonfirRedirectPage(pathname);

        if (shouldRedirectToFallbackCaseId) {
          navigateTo({
            pathname: ROUTES.ROOT,
            search: createQueryParams(encodeURIComponent(lastCaseId))
          });
        }
      },
      [isAuthenticated, queryParams.caseId, pathname, navigateTo]
    );

    const { loading } = useQuery<getScreeningCases>(GET_SCREENING_CASES_QUERY, {
      skip: !shouldCallCasesRequest,
      onCompleted: (data) => {
        redirectToFallbackCaseId(data.screeningCases ?? []);
      }
    });

    return { isLoading: loading };
  };
